import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import styles from "@/pages/css/ModelView.module.css";

/**
 * Props for the ModelView component
 */
interface ModelViewProps {
  /** URL to the 3D model file */
  fileUrl?: string;
  /** Uploaded 3D model file */
  file?: File;
  /** Optional MTL file for OBJ models */
  mtlFile?: File;
  /** Callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Callback when loading error occurs */
  onLoadingError?: (error: string) => void;
  /** Callback when loading succeeds */
  onLoadingSuccess?: () => void;
}

/**
 * A component for viewing 3D models in various formats (GLB, GLTF, OBJ, 3DM)
 */
export const ModelView: React.FC<ModelViewProps> = ({ 
  fileUrl, 
  file, 
  mtlFile,
  onLoadingChange,
  onLoadingError,
  onLoadingSuccess
}) => {
  // Refs
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const sceneRef = useRef<THREE.Scene>();
  const controlsRef = useRef<OrbitControls>();
  const moveIntervalRef = useRef<NodeJS.Timeout>();
  
  // State
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);

  /**
   * Get the file extension from the file URL or file object - IMPROVED VERSION
   */
  const getFileExtension = (): string | undefined => {
    if (file) {
      return file.name.split('.').pop()?.toLowerCase();
    } else if (fileUrl) {
      // Remove query parameters and hash
      const cleanUrl = fileUrl.split(/[?#]/)[0];
      // Get the last part of the path and extract extension
      return cleanUrl.split('.').pop()?.toLowerCase();
    }
    return undefined;
  };

  /**
   * Handle movement of the model along a specified direction
   */
  const handleMove = (direction: 'left' | 'right' | 'up' | 'down') => {
    if (!controlsRef.current) return;
    const moveAmount = 2; // Adjust this value to change movement speed
    const extension = getFileExtension();
    
    if (extension === '3dm') {
      // Adjust movement for 3DM files (Z-up coordinate system)
      switch (direction) {
        case 'left':
          controlsRef.current.target.x -= moveAmount;
          break;
        case 'right':
          controlsRef.current.target.x += moveAmount;
          break;
        case 'up':
          controlsRef.current.target.z += moveAmount; // Use Z axis for up/down
          break;
        case 'down':
          controlsRef.current.target.z -= moveAmount; // Use Z axis for up/down
          break;
      }
    } else {
      // Standard movement for other files (Y-up coordinate system)
      switch (direction) {
        case 'left':
          controlsRef.current.target.x -= moveAmount;
          break;
        case 'right':
          controlsRef.current.target.x += moveAmount;
          break;
        case 'up':
          controlsRef.current.target.y += moveAmount;
          break;
        case 'down':
          controlsRef.current.target.y -= moveAmount;
          break;
      }
    }
    controlsRef.current.update();
  };

  /**
   * Handle zoom in/out of the camera
   */
  const handleZoom = (zoomIn: boolean) => {
    if (!controlsRef.current || !cameraRef.current) return;
    const zoomSpeed = 1.2;
    
    if (zoomIn) {
      cameraRef.current.position.multiplyScalar(1 / zoomSpeed);
    } else {
      cameraRef.current.position.multiplyScalar(zoomSpeed);
    }
    controlsRef.current.update();
  };

  /**
   * Start continuous movement in a specified direction
   */
  const startMove = (direction: 'left' | 'right' | 'up' | 'down') => {
    // Clear any existing interval
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
    }

    // Initial move
    handleMove(direction);

    // Setup continuous movement
    moveIntervalRef.current = setInterval(() => {
      handleMove(direction);
    }, 50); // Adjust this value to control movement speed
  };

  /**
   * Stop continuous movement
   */
  const stopMove = () => {
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
    }
  };

  // Main effect for setting up Three.js scene and loading models
  useEffect(() => {
    if (!mountRef.current) return;

    // Initialize loading state
    setLoading(true);
    if (onLoadingChange) onLoadingChange(true);
    
    setError(null);
    setLoadingProgress(0);

    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);  // Set light gray background
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      10000
    );
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      preserveDrawingBuffer: true
    });
    rendererRef.current = renderer;
    renderer.setClearColor(0xf6f6f6);  // Light gray background
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    mountRef.current.appendChild(renderer.domElement);

    // Lighting - reverting to original settings that worked well
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1).normalize();
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);
    
    // Add a second directional light from another angle for better illumination
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-1, -1, 1).normalize();
    scene.add(directionalLight2);
    
    // Add a hemisphere light for more natural lighting with subtle color
    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x303030, 0.5);
    scene.add(hemisphereLight);

    // Controls with better defaults for 3D navigation
    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    
    const fileExtension = getFileExtension();

    // Set up coordinate system based on file type
    if (fileExtension === '3dm') {
      // For 3DM files we'll use Rhino's native coordinate system
      camera.up.set(0, 0, 1); // Z is up
      controls.object.up.set(0, 0, 1);
    } else if (fileExtension === 'obj') {
      // For OBJ files we typically use Y-up, but this can be customized
      camera.up.set(0, 1, 0); // Y is up (standard)
      controls.object.up.set(0, 1, 0);
    }

    // Improve controls for better mouse interaction
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.zoomSpeed = 1.0;
    controls.rotateSpeed = 0.8;
    controls.panSpeed = 0.8;
    controls.screenSpacePanning = true;
    controls.enablePan = true;
    controls.autoRotate = false;
    controls.minDistance = 0.1;
    controls.maxDistance = 1000;

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    
    // Handle loading errors
    const handleLoadingError = (error: any) => {
      console.error("Error loading model");
      const errorMessage = `Failed to load model: ${error.message || 'Unknown error'}`;
      setError(errorMessage);
      setLoading(false);
      
      if (onLoadingError) onLoadingError(errorMessage);
      if (onLoadingChange) onLoadingChange(false);
    };

    // Track loading progress
    const handleProgress = (progress: ProgressEvent) => {
      if (progress.lengthComputable) {
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        setLoadingProgress(percentage);
      }
    };

    // Helper function to process and add the model
    const processModel = (model: THREE.Object3D) => {
      
      // Check if model has any valid children
      let hasValidGeometry = false;
      
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Check if geometry is valid
          if (child.geometry && 
              child.geometry.attributes && 
              child.geometry.attributes.position && 
              child.geometry.attributes.position.count > 0) {
            
            hasValidGeometry = true;
            
            // Only set default material if none exists
            if (!child.material || 
                (Array.isArray(child.material) && child.material.length === 0)) {
              
              // Check the file extension to apply different materials based on file type
              const fileExt = getFileExtension();
              
              if (fileExt === 'obj') {
                // Specific material just for OBJ files
                child.material = new THREE.MeshStandardMaterial({
                  color: 0x444444,     // Darker gray for better contrast with light background
                  metalness: 0.3,
                  roughness: 0.7,
                  flatShading: false,
                  side: THREE.DoubleSide
                });
              } else {
                // Original material that was working well for GLTF/GLB/3DM
                child.material = new THREE.MeshPhongMaterial({
                  color: 0x808080,    // Medium gray
                  shininess: 30,      // Medium shine
                  specular: 0x111111, // Slight specular highlight
                  flatShading: false  // Smooth shading
                });
              }
            }
            
            // Handle multi-materials case
            if (Array.isArray(child.material)) {
              const fileExt = getFileExtension();
              
              for (let i = 0; i < child.material.length; i++) {
                if (!child.material[i]) {
                  if (fileExt === 'obj') {
                    // Darker material for OBJ files with more contrast
                    child.material[i] = new THREE.MeshStandardMaterial({
                      color: 0x444444,     // Darker gray - more contrast
                      metalness: 0.3,      // Some metalness
                      roughness: 0.7,      // Medium-high roughness
                      flatShading: false,  // Smooth shading
                      side: THREE.DoubleSide // Render both sides of faces
                    });
                  } else {
                    // Default material for other file types - keeping original
                    child.material[i] = new THREE.MeshPhongMaterial({
                      color: 0x808080,    // Medium gray
                      shininess: 30,      // Medium shine
                      specular: 0x111111, // Slight specular highlight
                      flatShading: false  // Smooth shading
                    });
                  }
                }
              }
            }
            
            // Enable shadows
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Fix potential issues with normals
            if (child.geometry.attributes.normal === undefined) {
              child.geometry.computeVertexNormals();
            }
          } else {
            console.warn('Mesh with invalid geometry found:', child);
          }
        }
      });
      
      // For OBJ files specifically, additional handling may be needed
      const fileExt = getFileExtension();
      if (fileExt === 'obj' && !hasValidGeometry) {
        
        // Try to extract any usable lines from the OBJ and create a simple mesh
        try {
          // Create a basic geometry from all positions found
          const positions: number[] = [];
          const indices: number[] = [];
          let vertexCount = 0;
          
          // Process each child to find any position data
          model.traverse((child: any) => {
            if (child.geometry && child.geometry.attributes && child.geometry.attributes.position) {
              const posArray = child.geometry.attributes.position.array;
              for (let i = 0; i < posArray.length; i++) {
                positions.push(posArray[i]);
              }
              
              // Create simple triangulation
              for (let i = 0; i < posArray.length / 3 - 2; i++) {
                indices.push(vertexCount);
                indices.push(vertexCount + i + 1);
                indices.push(vertexCount + i + 2);
              }
              
              vertexCount += posArray.length / 3;
            }
          });
          
          if (positions.length > 0) {
            // Create a new buffer geometry with the collected position data
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
            geometry.setIndex(indices);
            geometry.computeVertexNormals();
            
            // Create a mesh with this geometry
            const material = new THREE.MeshPhongMaterial({ 
              color: 0x808080,    // Medium gray
              shininess: 30,      // Medium shine
              specular: 0x111111, // Slight specular highlight
              flatShading: false, // Smooth shading
              side: THREE.DoubleSide  // Make it visible from both sides
            });
            
            const mesh = new THREE.Mesh(geometry, material);
            model.clear();  // Remove existing children
            model.add(mesh);
            
            hasValidGeometry = true;
          }
        } catch (e) {
          console.error('Failed to create fallback mesh');
        }
      }
      
      // Check if the model has valid geometry before adding it
      if (!hasValidGeometry) {
        console.error('No valid geometry found in the model');
        setError('The model contains no valid geometry to display');
        setLoading(false);
        if (onLoadingError) onLoadingError('The model contains no valid geometry to display');
        if (onLoadingChange) onLoadingChange(false);
        return;
      }
      
      // Fix any issues with model structure - sometimes 3DM models need extra processing
      if (getFileExtension() === '3dm') {
        // Create a clean group to transfer valid meshes
        const cleanGroup = new THREE.Group();
        model.traverse((child) => {
          if (child instanceof THREE.Mesh && 
              child.geometry && 
              child.geometry.attributes && 
              child.geometry.attributes.position) {
            cleanGroup.add(child.clone());
          }
        });
        
        // If we have valid geometry after filtering, use the clean group
        if (cleanGroup.children.length > 0) {
          model = cleanGroup;
        }
      }
      
      // Center the model
      const box = new THREE.Box3().setFromObject(model);
      
      // Check if the bounding box is valid (non-infinite)
      if (box.min.x !== Infinity && box.max.x !== -Infinity) {
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        scene.add(model);
      } else {
        console.warn('Model has an invalid bounding box, adding without centering');
        scene.add(model);
      }

      // Adjust camera position based on model size
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      cameraZ *= 1.5;  // Increased zoom out factor

      // Set initial camera position based on file type
      
      if (fileExtension === "3dm") {
        const distance = cameraZ * 0.7;  // Closer view
        camera.position.set(distance, distance, distance);
        camera.lookAt(0, 0, 0);
        
        // Ensure Z-up orientation
        camera.up.set(0, 0, 1);
        controls.update();
      } else if (fileExt === "obj") {
        const distance = cameraZ * 0.7;
        camera.position.set(distance, distance, distance);
        camera.lookAt(0, 0, 0);
      } else {
        camera.position.set(0, 0, cameraZ * 0.7);  // Closer view
      }

      // Update control limits
      controls.minDistance = cameraZ * 0.1;
      controls.maxDistance = cameraZ * 10;
      controls.target.set(0, 0, 0);
      controls.update();
      
      setLoading(false);
      if (onLoadingChange) onLoadingChange(false);
      if (onLoadingSuccess) onLoadingSuccess();
    };

    // Determine the URL and file extension
    let url: string | undefined;
    let mtlUrl: string | undefined;
    
    // Create object URLs for file uploads, or use direct URL for fileUrl
    if (file) {
      url = URL.createObjectURL(file);
      if (mtlFile) {
        mtlUrl = URL.createObjectURL(mtlFile);
      }
    } else if (fileUrl) {
      url = fileUrl;
    }

    // Get file extension using our improved function
    const extension = getFileExtension();
    
    // Load the model based on its file extension
    if (url && extension) {
      
      if (extension === "3dm") {
        
        // Try multiple versions of the Rhino3dm library to handle different file formats
        const tryRhinoLoad = (version: string) => {
          
          const rhinoLoader = new Rhino3dmLoader();
          rhinoLoader.setLibraryPath(`https://cdn.jsdelivr.net/npm/rhino3dm@${version}/`);
          
          // Note: setWasmPath is not available in all versions of THREE.js/Rhino3dmLoader
          // This is a workaround to handle different versions
          if (typeof (rhinoLoader as any).setWasmPath === 'function') {
            (rhinoLoader as any).setWasmPath(`https://cdn.jsdelivr.net/npm/rhino3dm@${version}/`);
          }
          
          return new Promise<THREE.Object3D>((resolve, reject) => {
            rhinoLoader.load(
              url as string,
              (object) => {                
                // Apply a rotation to the model if needed
                const rotationMatrix = new THREE.Matrix4();
                rotationMatrix.makeRotationX(-Math.PI / 2); // Flip to match normal conventions
                object.applyMatrix4(rotationMatrix);
                
                resolve(object);
              },
              handleProgress,
              (error) => {
                console.warn(`Loading failed with version ${version}:`, error);
                reject(error);
              }
            );
          });
        };
        
        // Set loading to show immediate feedback
        setLoading(true);
        
        // Try with different rhino3dm versions - newest first, then fallbacks
        tryRhinoLoad("8.9.0")
          .catch(() => tryRhinoLoad("7.15.0"))
          .catch(() => tryRhinoLoad("0.15.0"))
          .then((object) => {
            // Adjust the camera's up vector
            if (controlsRef.current && cameraRef.current) {
              cameraRef.current.up.set(0, 0, 1);
              controlsRef.current.object.up.set(0, 0, 1);
            }
            processModel(object);
          })
          .catch((error) => {
            console.error("All Rhino3dm versions failed to load the model:");
            
            // If all loading attempts fail, try a more simplified approach
            try {
              const finalAttemptLoader = new Rhino3dmLoader();
              finalAttemptLoader.setLibraryPath("https://cdn.jsdelivr.net/npm/rhino3dm@latest/");
              
              // Disable worker mode which can sometimes help
              // Use type assertion to access the property if it exists
              if (typeof (finalAttemptLoader as any).useWebWorker !== 'undefined') {
                (finalAttemptLoader as any).useWebWorker = false;
              }
              
              finalAttemptLoader.load(
                url as string,
                (object) => {
                  // Filter out any problematic parts (like annotations)
                  const filteredObject = new THREE.Group();
                  object.traverse((child) => {
                    // Only add meshes and valid geometries
                    if (child instanceof THREE.Mesh && 
                        child.geometry && 
                        child.geometry.attributes && 
                        child.geometry.attributes.position) {
                      filteredObject.add(child.clone());
                    }
                  });
                  
                  if (filteredObject.children.length === 0) {
                    handleLoadingError(new Error("No valid geometry found in the 3DM file"));
                  } else {
                    // Maintain Z-up orientation for the final attempt too
                    if (cameraRef.current && controlsRef.current) {
                      cameraRef.current.up.set(0, 0, 1);
                      controlsRef.current.object.up.set(0, 0, 1);
                    }
                    processModel(filteredObject);
                  }
                },
                handleProgress,
                (finalError) => {
                  handleLoadingError(finalError);
                }
              );
            } catch (finalCatchError) {
              handleLoadingError(finalCatchError);
            }
          });
      } else if (extension === "obj") {
        try {
          // Start loading indicator
          setLoading(true);
          
          // Create a new loader
          const objLoader = new OBJLoader();
          
          // Let's try loading the original file first without preprocessing
          // This might preserve important visual information
          objLoader.load(
            url as string,
            (object) => {
              
              // Apply a special material to all meshes to ensure visibility
              object.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                  // Create a material that's highly visible and looks good
                  const mat = new THREE.MeshStandardMaterial({
                    color: 0x555555,         // Medium-dark gray (more visible than white)
                    metalness: 0.1,          // Low metalness makes colors more true
                    roughness: 0.6,          // Medium roughness for natural look
                    flatShading: false,      // Smooth shading
                    side: THREE.DoubleSide   // Render both sides of faces
                  });
                  
                  // Replace any existing materials
                  if (Array.isArray(child.material)) {
                    for (let i = 0; i < child.material.length; i++) {
                      child.material[i] = mat;
                    }
                  } else {
                    child.material = mat;
                  }
                  
                  // Ensure normals are computed
                  if (!child.geometry.attributes.normal) {
                    child.geometry.computeVertexNormals();
                  }
                }
              });
              
              // Process and display the model
              processModel(object);
            },
            handleProgress,
            (error) => {
              console.error('Error loading OBJ directly');
              
              // If direct loading fails, try with preprocessing as fallback

              
              // Helper function to preprocess OBJ - improved to keep more command types
              const preprocessObjFile = async (url: string): Promise<string> => {
                try {
                  const response = await fetch(url as string);
                  const text = await response.text();
                  
                  // Filter out only clearly problematic commands but keep more data
                  // This allows more of the original file to be preserved
                  const lines = text.split('\n');
                  const filteredLines = lines.filter(line => {
                    const trimmed = line.trim();
                    if (!trimmed || trimmed.startsWith('#')) return true; // Keep comments and empty lines
                    
                    // Keep all standard OBJ commands plus some additional ones that might help
                    const firstWord = trimmed.split(' ')[0].toLowerCase();
                    return ['v', 'vn', 'vt', 'f', 'g', 'o', 'usemtl', 'mtllib', 's', 
                            'l', 'p', 'maplib', 'usemap', 'bevel', 'c_interp', 'd_interp', 
                            'lod', 'shadow_obj', 'trace_obj'].includes(firstWord);
                  });
                  
                  return filteredLines.join('\n');
                } catch (error) {
                  console.error("Error preprocessing OBJ file");
                  throw error;
                }
              };
              
              preprocessObjFile(url as string)
                .then(preprocessedContent => {
                  const blob = new Blob([preprocessedContent], { type: 'text/plain' });
                  const processedUrl = URL.createObjectURL(blob);
                  
                  objLoader.load(
                    processedUrl as string,
                    (object) => {
                      URL.revokeObjectURL(processedUrl);
                      
                      // Apply the same material treatment
                      object.traverse((child) => {
                        if (child instanceof THREE.Mesh) {
                          const mat = new THREE.MeshStandardMaterial({
                            color: 0x555555,
                            metalness: 0.1,
                            roughness: 0.6,
                            flatShading: false,
                            side: THREE.DoubleSide
                          });
                          
                          if (Array.isArray(child.material)) {
                            for (let i = 0; i < child.material.length; i++) {
                              child.material[i] = mat;
                            }
                          } else {
                            child.material = mat;
                          }
                          
                          if (!child.geometry.attributes.normal) {
                            child.geometry.computeVertexNormals();
                          }
                        }
                      });
                      
                      processModel(object);
                    },
                    handleProgress,
                    (finalError) => {
                      handleLoadingError(finalError);
                    }
                  );
                });
            }
          );
        } catch (error) {
          handleLoadingError(error);
        }
      } else if (extension === "gltf" || extension === "glb") {
        const loader = new GLTFLoader();
        loader.load(
          url as string,
          (gltf: GLTF) => {
            processModel(gltf.scene);
          },
          handleProgress,
          handleLoadingError
        );
      } else {
        setError(`Unsupported file format: ${extension}`);
        setLoading(false);
        if (onLoadingError) onLoadingError(`Unsupported file format: ${extension}`);
        if (onLoadingChange) onLoadingChange(false);
      }
    } else {
      setError("No valid file or URL provided");
      setLoading(false);
      if (onLoadingError) onLoadingError("No valid file or URL provided");
      if (onLoadingChange) onLoadingChange(false);
    }

    // Handle window resize
    const handleResize = () => {
      if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;
      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (mountRef.current && rendererRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
      if (file && url) {
        URL.revokeObjectURL(url);
      }
      if (mtlFile && mtlUrl) {
        URL.revokeObjectURL(mtlUrl);
      }
      if (moveIntervalRef.current) {
        clearInterval(moveIntervalRef.current);
      }
    };
  }, [fileUrl, file, mtlFile, onLoadingChange, onLoadingError, onLoadingSuccess]);

  return (
    <div className={styles.modelViewContainer}>
      <div ref={mountRef} className={styles.modelContainer} />
      
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading: {loadingProgress}%</div>
        </div>
      )}
      
      {error && (
        <div className={styles.errorOverlay}>
          <div className={styles.errorMessage}>{error}</div>
        </div>
      )}
    </div>
  );
};